import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import OTPLoginModal from './OTPLoginModal';
import { Button } from './ui/button';
import { 
  Users, MapPin, Clock, Calendar as CalendarIcon, Trophy, 
  AlertCircle, CheckCircle, Loader2, ArrowLeft, IndianRupee,
  Share2, Check, ExternalLink
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { getMapUrlBySlug } from '../config/venueConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_KEY = process.env.REACT_APP_COMMUNITY_GAMES_RAZORPAY_KEY_ID || process.env.REACT_APP_RAZORPAY_KEY_ID;

const CommunityGamePublic = () => {
  const { venueName, gameId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useCustomerAuth();

  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('details'); // details | paying | success | waitlist_success | error
  const [showLogin, setShowLogin] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelResult, setCancelResult] = useState(null);
  // Pending intent set when an unauthenticated user clicks Join/Waitlist;
  // resumed after OTP login completes.
  const [pendingIntent, setPendingIntent] = useState(null); // null | 'join' | 'waitlist'

  const loadGame = useCallback(async () => {
    try {
      // Pass phone (if known) so backend returns `viewer_status` for personal banner.
      const phoneParam = user?.phone
        ? `?phone=${encodeURIComponent((user.phone || '').replace('+91','').replace('+','').replace(/\s/g,''))}`
        : '';
      const res = await fetch(`${API_URL}/api/community-games/public/${gameId}${phoneParam}`);
      if (!res.ok) throw new Error('Game not found');
      const data = await res.json();
      setGame(data);
    } catch (err) {
      setError('Game not found or has been removed.');
    } finally {
      setLoading(false);
    }
  }, [gameId, user?.phone]);

  useEffect(() => { loadGame(); }, [loadGame]);

  // Check if current user already joined
  const hasJoined = (() => {
    if (!user || !game?.player_phones) return false;
    const userPhone = (user.phone || '').replace('+91','').replace('+','').replace(/\s/g,'');
    return game.player_phones.includes(userPhone);
  })();

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const handleJoinAndPay = async (overrideUser, isWaitlist = false) => {
    const currentUser = overrideUser || user;
    if (!currentUser?.name) {
      toast({ title: 'Error', description: 'Please complete your profile first', variant: 'destructive' });
      return;
    }

    setStep('paying');
    setProcessing(true);

    try {
      const phone = currentUser.phone;
      
      const res = await fetch(`${API_URL}/api/community-games/public/${gameId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: currentUser.name,
          phone: phone,
          is_waitlist: isWaitlist,
        }),
      });

      if (!res.ok) {
        let errMsg = 'Failed to join game';
        try {
          const err = await res.json();
          errMsg = err.detail || errMsg;
        } catch { /* non-JSON response */ }
        throw new Error(errMsg);
      }

      const orderData = await res.json();

      const options = {
        key: RAZORPAY_KEY,
        amount: orderData.amount,
        currency: orderData.currency,
        name: game.venue_name,
        description: `${isWaitlist ? 'Waitlist - ' : ''}Community Game: ${game.sport}${game.format ? ` ${game.format}` : ''}${game.venue_name ? ` - ${game.venue_name}` : ''}`,
        order_id: orderData.order_id,
        prefill: {
          contact: `+91${phone}`,
          name: currentUser.name,
        },
        theme: { color: '#111827' },
        handler: async function(response) {
          try {
            const verifyRes = await fetch(`${API_URL}/api/community-games/public/${gameId}/verify-payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });

            if (!verifyRes.ok) {
              let errMsg = 'Payment verification failed';
              try {
                const err = await verifyRes.json();
                errMsg = err.detail || errMsg;
              } catch { /* non-JSON response */ }
              throw new Error(errMsg);
            }

            setStep(isWaitlist ? 'waitlist_success' : 'success');
            setProcessing(false);
            loadGame();
          } catch (err) {
            setStep('error');
            setError(err.message);
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: function() {
            setStep('details');
            setProcessing(false);
            toast({ title: 'Payment Cancelled', description: 'You can try again anytime.', variant: 'destructive' });
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
      setStep('details');
      setProcessing(false);
    }
  };

  const handleJoinClick = () => {
    if (isAuthenticated) {
      handleJoinAndPay(null, false);
    } else {
      setPendingIntent('join');
      setShowLogin(true);
    }
  };

  const handleJoinWaitlistClick = () => {
    if (isAuthenticated) {
      handleJoinAndPay(null, true);
    } else {
      setPendingIntent('waitlist');
      setShowLogin(true);
    }
  };

  // After login, proceed to the intent the user originally clicked.
  const handleLoginSuccess = (userData) => {
    setShowLogin(false);
    const intent = pendingIntent;
    setPendingIntent(null);
    setTimeout(() => handleJoinAndPay(userData, intent === 'waitlist'), 200);
  };

  const getWhatsAppShareUrl = () => {
    const gameUrl = `${window.location.origin}/community/${game.venue_slug || venueName}/game/${game.id}`;
    const text = `Hey! Join me for *${game.sport}${game.format ? ` ${game.format}` : ''}* at *${game.venue_name}*\n\n` +
      `${formatDate(game.date)}\n` +
      `${game.start_time} - ${game.end_time}\n` +
      `${game.cost_per_player ? `₹${game.cost_per_player} per player` : ''}\n` +
      `${game.spots_left > 0 ? `${game.spots_left} spots left!` : 'Game is full'}\n\n` +
      `Join here: ${gameUrl}`;
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error && !game) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
        <p className="text-gray-700 font-medium text-center">{error}</p>
        <Button variant="outline" onClick={() => navigate('/')} className="mt-4">
          Go Home
        </Button>
      </div>
    );
  }

  // Success screen (handles both confirmed-spot success and paid-waitlist success)
  if (step === 'success' || step === 'waitlist_success') {
    const isWaitlistSuccess = step === 'waitlist_success';
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6" data-testid={isWaitlistSuccess ? 'waitlist-join-success' : 'join-success'}>
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-sm">
          <div className={`w-16 h-16 ${isWaitlistSuccess ? 'bg-amber-100' : 'bg-green-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
            <CheckCircle className={`h-8 w-8 ${isWaitlistSuccess ? 'bg-amber-600 text-white rounded-full' : 'text-green-600'}`} />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {isWaitlistSuccess ? 'You\'re on the Waitlist!' : 'You\'re In!'}
          </h1>
          <p className="text-gray-500 text-sm mb-6">
            {isWaitlistSuccess
              ? `Payment received. We'll auto-promote you the moment a confirmed player drops out — or auto-refund 2 hours before the game if no spot opens.`
              : `You've successfully joined the ${game.sport}${game.format ? ` ${game.format}` : ''} game.`}
          </p>
          <div className="bg-gray-50 rounded-xl p-4 text-left space-y-2 text-sm mb-6">
            <div className="flex justify-between">
              <span className="text-gray-500">Venue</span>
              <span className="font-medium">{game.venue_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Date</span>
              <span className="font-medium">{formatDate(game.date)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Time</span>
              <span className="font-medium">{game.start_time} - {game.end_time}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Amount Paid</span>
              <span className={`font-medium ${isWaitlistSuccess ? 'text-amber-700' : 'text-green-600'}`}>&#8377;{game.cost_per_player}</span>
            </div>
            {isWaitlistSuccess && (
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <span className="font-medium text-amber-700">Waitlisted</span>
              </div>
            )}
          </div>
          <Button onClick={() => navigate('/')} className="w-full">
            Back to Home
          </Button>
          <a
            href={getWhatsAppShareUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#1ebe57] text-white font-medium py-2.5 rounded-lg mt-3 transition-colors text-sm"
            data-testid="success-share-whatsapp"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Invite friends on WhatsApp
          </a>
        </div>
      </div>
    );
  }

  // Error screen
  if (step === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-sm">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h1>
          <p className="text-gray-500 text-sm mb-6">{error}</p>
          <Button onClick={() => { setStep('details'); setError(''); }} className="w-full">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const isFull = game.spots_left <= 0;
  const isCancelled = game.status === 'cancelled';
  const isCompleted = game.status === 'completed';
  const confirmedPlayers = game.confirmed_players || [];
  const waitlistCount = game.waitlist_count || 0;
  const viewerStatus = game.viewer_status || null;
  const isViewerOnWaitlist = viewerStatus?.role === 'waitlist';
  const isViewerWaitlistExpired = viewerStatus?.role === 'waitlist_expired';
  const isViewerWaitlistCancelled = viewerStatus?.role === 'waitlist_cancelled';
  const isViewerPromoted = viewerStatus?.role === 'confirmed' && viewerStatus?.was_promoted;

  const handleSelfCancel = async () => {
    if (!user) return;
    setCancelling(true);
    try {
      const res = await fetch(`${API_URL}/api/community-games/public/${game.id}/cancel-participant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: user.phone || '' })
      });
      const data = await res.json();
      if (res.ok) {
        setCancelResult(data);
        setShowCancelConfirm(false);
        toast({
          title: 'Cancellation Successful',
          description: data.refund_amount > 0
            ? `Refund of ₹${data.refund_amount.toFixed(0)} (${data.refund_percentage}%) initiated.`
            : 'Cancelled. No refund applicable per policy.',
        });
        loadGame();
      } else {
        toast({ title: 'Error', description: data.detail || 'Cancel failed', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="community-game-public">
      {/* OTP Login Modal */}
      {showLogin && (
        <OTPLoginModal
          onClose={() => { setShowLogin(false); setPendingIntent(null); }}
          onSuccess={handleLoginSuccess}
        />
      )}

      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/')} className="p-1 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <p className="font-semibold text-sm">{game.venue_name}</p>
            <p className="text-xs text-gray-500">Community Game</p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 pb-44">
        {/* Sport + Format */}
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-gray-700" />
            <h1 className="text-2xl font-bold capitalize text-gray-900">
              {game.sport}{game.format ? ` ${game.format}` : ''}
            </h1>
          </div>
          {game.skill_level && (
            <span className="inline-block mt-2 px-3 py-1 bg-gray-100 rounded-full text-xs font-medium capitalize text-gray-600">
              Level of Game: {game.skill_level}
            </span>
          )}
        </div>

        {/* Info Cards */}
        <div className="space-y-3 mb-6">
          <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <CalendarIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium">{formatDate(game.date)}</p>
              <p className="text-xs text-gray-500">{game.start_time} - {game.end_time}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0A2C93]/5 rounded-lg flex items-center justify-center">
              <MapPin className="h-5 w-5 text-[#0A2C93]" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{game.venue_name}</p>
              {game.court_name && <p className="text-xs text-gray-500">{game.court_name}</p>}
              {game.location && <p className="text-xs text-gray-500">{game.location}</p>}
            </div>
            {getMapUrlBySlug(game.venue_slug || venueName) && (
              <a href={getMapUrlBySlug(game.venue_slug || venueName)} target="_blank" rel="noopener noreferrer"
                className="flex-shrink-0 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs font-medium flex items-center gap-1 hover:bg-blue-100 transition"
                data-testid="map-link">
                <ExternalLink className="h-3 w-3" /> Map
              </a>
            )}
          </div>

          <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
              <Users className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium">{game.players_filled} / {game.max_players} Players</p>
              <p className="text-xs text-gray-500">
                {isFull ? 'Game is full' : `${game.spots_left} spot${game.spots_left > 1 ? 's' : ''} left`}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
              <IndianRupee className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-medium">&#8377;{game.cost_per_player} per player</p>
              <p className="text-xs text-gray-500">Pay online to confirm your spot</p>
            </div>
          </div>
        </div>

        {game.description && (
          <div className="bg-white rounded-xl border p-4 mb-4">
            <p className="text-xs text-gray-500 mb-1">About this game</p>
            <p className="text-sm text-gray-700">{game.description}</p>
          </div>
        )}

        {/* Confirmed Players */}
        {confirmedPlayers.length > 0 && (
          <div className="bg-white rounded-xl border p-4 mb-4" data-testid="confirmed-players">
            <p className="text-xs font-medium text-gray-500 mb-3">Confirmed Players ({confirmedPlayers.length})</p>
            <div className="flex flex-wrap gap-2">
              {confirmedPlayers.map((player, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50 border border-gray-100">
                  <div className="w-6 h-6 rounded-full bg-[#0A2C93] flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] font-bold text-white">{player.initial}</span>
                  </div>
                  <span className="text-xs font-medium text-gray-700">{player.name}</span>
                </div>
              ))}
              {game.spots_left > 0 && Array.from({ length: Math.min(game.spots_left, 3) }).map((_, i) => (
                <div key={`empty-${i}`} className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-dashed border-gray-200">
                  <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] text-gray-400">?</span>
                  </div>
                  <span className="text-xs text-gray-400">Open spot</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Waitlist Info */}
        {isFull && waitlistCount > 0 && (
          <div className="bg-amber-50 rounded-xl border border-amber-100 p-3 mb-4 text-center" data-testid="waitlist-info">
            <p className="text-xs font-medium text-amber-700">{waitlistCount} player{waitlistCount > 1 ? 's' : ''} on waitlist</p>
          </div>
        )}

        {/* ============== Personal Viewer Status Banners ============== */}
        {/* You were promoted from waitlist -> confirmed */}
        {isViewerPromoted && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4" data-testid="viewer-banner-promoted">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm text-emerald-800">You've been promoted from the waitlist!</p>
                <p className="text-xs text-emerald-700 mt-1">A spot opened up and you're now a confirmed player. See you on the field.</p>
              </div>
            </div>
          </div>
        )}

        {/* You're on the paid waitlist (active) */}
        {isViewerOnWaitlist && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4" data-testid="viewer-banner-waitlist">
            <div className="flex items-start gap-3">
              <Users className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-sm text-amber-900">You're on the waitlist · Position #{viewerStatus?.waitlist_position || '?'}</p>
                <p className="text-xs text-amber-800 mt-1">You'll be promoted automatically if a confirmed player drops out. If no spot opens, you'll be fully refunded 2 hours before the game.</p>
              </div>
            </div>
          </div>
        )}

        {/* Your waitlist auto-refunded (2h before game) */}
        {isViewerWaitlistExpired && (
          <div className="bg-gray-100 border border-gray-300 rounded-xl p-4 mb-4" data-testid="viewer-banner-expired">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm text-gray-800">Waitlist closed — refund issued</p>
                <p className="text-xs text-gray-600 mt-1">
                  No spot opened up for you. Your &#8377;{viewerStatus?.amount_paid?.toFixed?.(0) || game.cost_per_player} has been refunded ({viewerStatus?.refund_status || 'processed'}). Refunds reach your account in 5–7 business days.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* You withdrew from the waitlist yourself */}
        {isViewerWaitlistCancelled && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4" data-testid="viewer-banner-cancelled">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm text-red-800">You withdrew from the waitlist</p>
                <p className="text-xs text-red-700 mt-1">Your full refund has been initiated ({viewerStatus?.refund_status || 'processed'}). Refunds arrive in 5–7 business days.</p>
              </div>
            </div>
          </div>
        )}

        {/* Auto-cancel warning */}
        {game.auto_cancel_enabled && game.min_players > 0 && (
          <div className="bg-blue-50 rounded-xl border border-blue-100 p-3 mb-4 text-center" data-testid="auto-cancel-info">
            <p className="text-xs text-blue-700">
              Min {game.min_players} players needed
              {game.auto_cancel_deadline_hours ? ` — auto-cancels ${game.auto_cancel_deadline_hours}h before start if not met` : ''}
            </p>
          </div>
        )}

        {/* Cancellation Policy (visible for active games, hidden when viewer is on waitlist — waitlist gets full refund anytime before cutoff) */}
        {!isCancelled && !isCompleted && !isViewerOnWaitlist && (
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 mb-4 text-xs text-amber-800" data-testid="cancellation-policy-public">
            <p className="font-semibold mb-1.5">Cancellation Policy</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>More than 6 hours before game: <strong>100% refund</strong></li>
              <li>3 to 6 hours before game: <strong>50% refund</strong></li>
              <li>Less than 3 hours before game: <strong>No refund</strong></li>
            </ul>
            {hasJoined && typeof game.current_refund_percentage === 'number' && game.hours_until_game > 0 && (
              <p className="mt-2 text-amber-900">
                If you cancel now ({game.hours_until_game.toFixed(1)}h to start): <strong>{game.current_refund_percentage}% refund</strong> (₹{(game.cost_per_player * game.current_refund_percentage / 100).toFixed(0)})
              </p>
            )}
          </div>
        )}

        {/* Share to WhatsApp */}
        <a
          href={getWhatsAppShareUrl()}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#1ebe57] text-white font-medium py-3 rounded-xl mb-6 transition-colors"
          data-testid="share-whatsapp-btn"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          Share on WhatsApp
        </a>

        {/* Paying State */}
        {step === 'paying' && (
          <div className="bg-white rounded-xl border p-6 mb-6 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-700">Processing payment...</p>
            <p className="text-xs text-gray-500 mt-1">Please complete the payment in the Razorpay window</p>
          </div>
        )}
      </div>

      {/* Bottom CTA */}
      {step === 'details' && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 pb-16 z-20">
          <div className="max-w-lg mx-auto">
            {isCancelled ? (
              <Button disabled className="w-full" variant="outline">
                <AlertCircle className="h-4 w-4 mr-2" /> Game Cancelled
              </Button>
            ) : isCompleted ? (
              <Button disabled className="w-full" variant="outline">
                Game Completed
              </Button>
            ) : hasJoined ? (
              <div data-testid="already-joined-state">
                <div className="flex items-center justify-center gap-2 w-full py-3 bg-emerald-50 border border-emerald-200 rounded-xl mb-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  <span className="font-semibold text-emerald-700">You've already joined this game!</span>
                </div>
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="flex items-center justify-center gap-2 w-full py-3 bg-white border border-red-200 text-red-600 hover:bg-red-50 rounded-xl mb-2 font-medium text-sm transition-colors"
                  data-testid="cancel-participation-btn"
                >
                  <AlertCircle className="h-4 w-4" /> Cancel My Participation
                </button>
                <a
                  href={getWhatsAppShareUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#1ebe57] text-white font-medium py-3 rounded-xl transition-colors"
                >
                  <Share2 className="h-4 w-4" /> Invite Friends
                </a>
              </div>
            ) : isViewerOnWaitlist ? (
              <div data-testid="already-on-waitlist-state">
                <div className="flex items-center justify-center gap-2 w-full py-3 bg-amber-50 border border-amber-200 rounded-xl mb-2">
                  <Users className="h-5 w-5 text-amber-600" />
                  <span className="font-semibold text-amber-800">You're on the waitlist · #{viewerStatus?.waitlist_position || '?'}</span>
                </div>
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="flex items-center justify-center gap-2 w-full py-3 bg-white border border-red-200 text-red-600 hover:bg-red-50 rounded-xl mb-2 font-medium text-sm transition-colors"
                  data-testid="withdraw-waitlist-btn"
                >
                  <AlertCircle className="h-4 w-4" /> Withdraw &amp; Get Full Refund
                </button>
              </div>
            ) : isFull ? (
              <div data-testid="join-waitlist-section">
                {/* Pre-payment amber info box */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3 text-xs text-amber-800" data-testid="waitlist-prepay-info">
                  <p className="font-semibold mb-1">How the waitlist works</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>Pay &#8377;{game.cost_per_player} now to lock your waitlist spot.</li>
                    <li>If a confirmed player drops out, you're auto-promoted (no extra charge).</li>
                    <li>If no spot opens, you get a <strong>full refund 2 hours before game start</strong>.</li>
                    <li>Withdraw anytime before the cutoff for a full refund.</li>
                  </ul>
                </div>
                <Button
                  onClick={handleJoinWaitlistClick}
                  className="w-full h-12 text-base font-semibold bg-amber-500 hover:bg-amber-600"
                  data-testid="join-waitlist-btn"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Join Waitlist &amp; Pay &#8377;{game.cost_per_player}
                </Button>
              </div>
            ) : (
              <Button onClick={handleJoinClick} className="w-full h-12 text-base font-semibold" data-testid="join-game-btn">
                Join Game &amp; Pay &#8377;{game.cost_per_player}
              </Button>
            )}
            {!hasJoined && !isViewerOnWaitlist && !isFull && (
              <p className="text-[10px] text-gray-400 text-center mt-2">
                If the game is cancelled, your payment will be refunded automatically.
              </p>
            )}
          </div>
        </div>
      )}
      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4" data-testid="cancel-confirm-modal">
          <div className="bg-white rounded-2xl w-full max-w-sm p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-base">{isViewerOnWaitlist ? 'Withdraw from Waitlist?' : 'Cancel Your Spot?'}</h3>
              <button onClick={() => setShowCancelConfirm(false)} className="p-1 hover:bg-gray-100 rounded">
                <ArrowLeft className="h-4 w-4 rotate-45" />
              </button>
            </div>
            {isViewerOnWaitlist ? (
              <p className="text-sm text-gray-600">
                You will receive a <strong>full refund of &#8377;{game.cost_per_player}</strong>. Waitlist withdrawals are always refunded in full.
              </p>
            ) : (
              <p className="text-sm text-gray-600">
                You will receive a <strong>{game.current_refund_percentage || 0}% refund</strong> ({(game.current_refund_percentage || 0) > 0 ? `₹${(game.cost_per_player * (game.current_refund_percentage || 0) / 100).toFixed(0)}` : 'no refund'}) based on our cancellation policy.
              </p>
            )}
            <p className="text-xs text-gray-500">
              Your spot will be released for other players. Refunds are processed within 5-7 business days.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowCancelConfirm(false)} className="flex-1" data-testid="cancel-no-btn">
                {isViewerOnWaitlist ? 'Stay on Waitlist' : 'Keep My Spot'}
              </Button>
              <Button onClick={handleSelfCancel} disabled={cancelling} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="cancel-confirm-btn">
                {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : (isViewerOnWaitlist ? 'Withdraw & Refund' : 'Cancel & Refund')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cancellation Result Toast/Banner */}
      {cancelResult && (
        <div className="fixed top-16 left-4 right-4 z-50 max-w-md mx-auto" data-testid="cancel-result-banner">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 shadow-lg">
            <div className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-sm text-emerald-800">Cancellation Successful</p>
                <p className="text-xs text-emerald-700 mt-1">
                  {cancelResult.refund_amount > 0
                    ? `Refund of ₹${cancelResult.refund_amount.toFixed(0)} (${cancelResult.refund_percentage}%) will arrive in 5-7 business days.`
                    : 'Cancelled. No refund per policy.'}
                </p>
              </div>
              <button onClick={() => setCancelResult(null)} className="p-1">
                <Check className="h-4 w-4 text-emerald-700" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityGamePublic;
